import {
  doc,
  setDoc,
  getDoc,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db, auth, COLLECTIONS } from '../config/firebase';

// Serviço para gerenciar dados do Firebase
export class FirebaseService {
  
  // ==================== USUÁRIOS ====================
  
  /**
   * Criar perfil inicial do usuário
   */
  static async createUserProfile(userId, userData) {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const userProfile = {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        plan: 'free', // free, basic, professional, enterprise
        onboardingCompleted: false,
        lastLogin: serverTimestamp()
      };
      
      await setDoc(userRef, userProfile);
      console.log('✅ Perfil do usuário criado:', userId);
      return userProfile;
    } catch (error) {
      console.error('❌ Erro ao criar perfil do usuário:', error);
      throw error;
    }
  }

  /**
   * Buscar perfil do usuário
   */
  static async getUserProfile(userId) {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return { id: userSnap.id, ...userSnap.data() };
      } else {
        console.log('👤 Usuário não encontrado, criando perfil...');
        return null;
      }
    } catch (error) {
      console.error('❌ Erro ao buscar perfil do usuário:', error);
      throw error;
    }
  }

  /**
   * Atualizar perfil do usuário
   */
  static async updateUserProfile(userId, updates) {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);

      // Verificar se o documento existe primeiro
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        // Documento existe, fazer update
        await updateDoc(userRef, {
          ...updates,
          updatedAt: serverTimestamp()
        });
        console.log('✅ Perfil do usuário atualizado:', userId);
      } else {
        // Documento não existe, criar novo
        console.log('👤 Perfil não existe, criando novo...');
        await setDoc(userRef, {
          ...updates,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isActive: true,
          plan: 'free'
        });
        console.log('✅ Perfil do usuário criado:', userId);
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar perfil do usuário:', error);
      console.error('Detalhes do erro:', error.message);
      throw error;
    }
  }

  // ==================== OABs DO USUÁRIO ====================

  /**
   * Adicionar OAB do usuário
   */
  static async addUserOAB(userId, oabData) {
    try {
      // Validar dados de entrada - aceitar tanto 'uf' quanto 'seccional'
      const seccional = oabData.seccional || oabData.uf;
      if (!oabData || !oabData.numero || !seccional) {
        throw new Error('Dados da OAB incompletos. Número e UF/seccional são obrigatórios.');
      }

      // Usar subcoleção: users/{userId}/oabs
      const oabRef = collection(db, 'users', userId, 'oabs');
      const oab = {
        numero: oabData.numero,
        seccional: seccional.toString().toUpperCase(),
        isActive: true,
        createdAt: serverTimestamp(),
        lastSync: null,
        syncStatus: 'pending' // pending, syncing, completed, error
      };

      const docRef = await addDoc(oabRef, oab);
      console.log('✅ OAB adicionada:', docRef.id);
      return { id: docRef.id, ...oab };
    } catch (error) {
      console.error('❌ Erro ao adicionar OAB:', error);
      throw error;
    }
  }

  /**
   * Buscar OABs do usuário
   */
  static async getUserOABs(userId) {
    try {
      // Usar subcoleção: users/{userId}/oabs
      const oabsRef = collection(db, 'users', userId, 'oabs');
      const q = query(
        oabsRef,
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const oabs = [];
      querySnapshot.forEach((doc) => {
        oabs.push({ id: doc.id, ...doc.data() });
      });

      return oabs;
    } catch (error) {
      console.error('❌ Erro ao buscar OABs do usuário:', error);
      throw error;
    }
  }

  /**
   * Remover OAB do usuário
   */
  static async removeUserOAB(userId, oabId) {
    try {
      // Usar subcoleção: users/{userId}/oabs/{oabId}
      const oabRef = doc(db, 'users', userId, 'oabs', oabId);
      await updateDoc(oabRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      });
      console.log('✅ OAB removida:', oabId);
    } catch (error) {
      console.error('❌ Erro ao remover OAB:', error);
      throw error;
    }
  }

  // ==================== CONFIGURAÇÕES DE MONITORAMENTO ====================

  /**
   * Salvar configurações de monitoramento
   */
  static async saveMonitoringSettings(userId, settings) {
    try {
      // Usar subcoleção: users/{userId}/settings/monitoring
      const settingsRef = doc(db, 'users', userId, 'settings', 'monitoring');
      const monitoringSettings = {
        // Período inicial para primeira sincronização
        initialSyncPeriod: {
          startDate: settings.startDate,
          endDate: settings.endDate
        },
        // Horários para sincronização automática (até 3)
        syncSchedule: settings.syncSchedule || [], // ['08:00', '14:00', '18:00']
        // Configurações de notificação
        notifications: {
          email: settings.notifications?.email || true,
          push: settings.notifications?.push || false,
          sms: settings.notifications?.sms || false
        },
        // Configurações gerais
        autoSync: settings.autoSync !== false, // true por padrão
        timezone: settings.timezone || 'America/Sao_Paulo',
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(settingsRef, monitoringSettings, { merge: true });
      console.log('✅ Configurações de monitoramento salvas:', userId);
      return monitoringSettings;
    } catch (error) {
      console.error('❌ Erro ao salvar configurações de monitoramento:', error);
      throw error;
    }
  }

  /**
   * Buscar configurações de monitoramento
   */
  static async getMonitoringSettings(userId) {
    try {
      // Usar subcoleção: users/{userId}/settings/monitoring
      const settingsRef = doc(db, 'users', userId, 'settings', 'monitoring');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        return { id: settingsSnap.id, ...settingsSnap.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('❌ Erro ao buscar configurações de monitoramento:', error);
      throw error;
    }
  }

  // ==================== PROCESSOS DO USUÁRIO ====================

  /**
   * Adicionar processo do usuário
   */
  static async addUserProcesso(userId, processoData) {
    try {
      // Usar subcoleção: users/{userId}/processos
      const processosRef = collection(db, 'users', userId, 'processos');
      const processo = {
        ...processoData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true
      };

      const docRef = await addDoc(processosRef, processo);
      console.log('✅ Processo adicionado:', docRef.id);
      return { id: docRef.id, ...processo };
    } catch (error) {
      console.error('❌ Erro ao adicionar processo:', error);
      throw error;
    }
  }

  /**
   * Buscar processos do usuário
   */
  static async getUserProcessos(userId) {
    try {
      // Usar subcoleção: users/{userId}/processos
      const processosRef = collection(db, 'users', userId, 'processos');
      const q = query(
        processosRef,
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const processos = [];
      querySnapshot.forEach((doc) => {
        processos.push({ id: doc.id, ...doc.data() });
      });

      return processos;
    } catch (error) {
      console.error('❌ Erro ao buscar processos do usuário:', error);
      throw error;
    }
  }

  /**
   * Atualizar processo do usuário
   */
  static async updateUserProcesso(userId, processoId, updates) {
    try {
      // Usar subcoleção: users/{userId}/processos/{processoId}
      const processoRef = doc(db, 'users', userId, 'processos', processoId);
      await updateDoc(processoRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      console.log('✅ Processo atualizado:', processoId);
    } catch (error) {
      console.error('❌ Erro ao atualizar processo:', error);
      throw error;
    }
  }

  // ==================== COMUNICAÇÕES PJE ====================

  /**
   * Salvar comunicações PJe no cache
   */
  static async savePjeComunicacoes(userId, oabInfo, comunicacoes) {
    try {
      // Verificar autenticação
      console.log('🔐 Verificando autenticação para salvar comunicações...');
      console.log('🔐 Auth atual:', auth.currentUser ? 'Logado' : 'Não logado');
      console.log('🔐 UID:', auth.currentUser?.uid);
      console.log('🔐 UserId parâmetro:', userId);

      if (!auth.currentUser) {
        throw new Error('Usuário não autenticado');
      }

      const batch = [];

      for (const comunicacao of comunicacoes) {
        // Gerar ID único e válido para o documento
        const docId = comunicacao.id || comunicacao.hash || `${oabInfo.numero}_${oabInfo.seccional}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Garantir que o ID seja uma string válida
        const validDocId = String(docId).replace(/[^a-zA-Z0-9_-]/g, '_');

        console.log('📄 Criando documento:', validDocId);
        const comunicacaoRef = doc(db, COLLECTIONS.PJE_COMUNICACOES, validDocId);
        const comunicacaoData = {
          ...comunicacao,
          id: validDocId, // Garantir que o ID seja salvo no documento
          userId,
          oabNumero: oabInfo.numero,
          oabSeccional: oabInfo.seccional,
          fetchedAt: serverTimestamp(),
          isRead: false,
          isImportant: false,
          tags: []
        };
        
        batch.push({ ref: comunicacaoRef, data: comunicacaoData });
      }
      
      // Salvar em lotes para melhor performance
      for (const item of batch) {
        await setDoc(item.ref, item.data, { merge: true });
      }
      
      console.log(`✅ ${comunicacoes.length} comunicações PJe salvas para usuário ${userId}`);
      return comunicacoes.length;
    } catch (error) {
      console.error('❌ Erro ao salvar comunicações PJe:', error);
      throw error;
    }
  }

  /**
   * Buscar comunicações PJe do usuário
   */
  static async getUserPjeComunicacoes(userId, filters = {}) {
    try {
      const comunicacoesRef = collection(db, COLLECTIONS.PJE_COMUNICACOES);
      let q = query(
        comunicacoesRef,
        where('userId', '==', userId),
        orderBy('data_disponibilizacao', 'desc')
      );
      
      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }
      
      const querySnapshot = await getDocs(q);
      const comunicacoes = [];
      querySnapshot.forEach((doc) => {
        const comunicacao = { id: doc.id, ...doc.data() };
        console.log('📧 Comunicação carregada - ID:', doc.id, typeof doc.id);
        comunicacoes.push(comunicacao);
      });

      console.log('📧 Total de comunicações carregadas:', comunicacoes.length);
      return comunicacoes;
    } catch (error) {
      console.error('❌ Erro ao buscar comunicações PJe do usuário:', error);
      throw error;
    }
  }

  // ==================== LISTENERS EM TEMPO REAL ====================

  /**
   * Escutar mudanças nos processos do usuário em tempo real
   */
  static subscribeToUserProcessos(userId, callback) {
    try {
      const processosRef = collection(db, COLLECTIONS.USER_PROCESSOS);
      const q = query(
        processosRef,
        where('userId', '==', userId),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      return onSnapshot(q, (querySnapshot) => {
        const processos = [];
        querySnapshot.forEach((doc) => {
          processos.push({ id: doc.id, ...doc.data() });
        });
        callback(processos);
      });
    } catch (error) {
      console.error('❌ Erro ao escutar processos do usuário:', error);
      throw error;
    }
  }

  /**
   * Escutar mudanças nas comunicações PJe do usuário em tempo real
   */
  static subscribeToUserPjeComunicacoes(userId, callback) {
    try {
      const comunicacoesRef = collection(db, COLLECTIONS.PJE_COMUNICACOES);
      const q = query(
        comunicacoesRef,
        where('userId', '==', userId),
        limit(50) // Limitar para performance
      );

      return onSnapshot(q, (querySnapshot) => {
        const comunicacoes = [];
        querySnapshot.forEach((doc) => {
          const comunicacao = { id: doc.id, ...doc.data() };
          console.log('🔄 Listener - ID:', doc.id, typeof doc.id);
          comunicacoes.push(comunicacao);
        });
        console.log('🔄 Listener: Comunicações carregadas:', comunicacoes.length);
        callback(comunicacoes);
      });
    } catch (error) {
      console.error('❌ Erro ao escutar comunicações PJe do usuário:', error);
      // Fallback: tentar carregar sem listener
      this.getUserPjeComunicacoes(userId).then(callback).catch(console.error);
    }
  }

  /**
   * Marcar comunicação como lida
   */
  static async markComunicacaoAsRead(userId, comunicacaoId) {
    try {
      // Validar e converter o ID para string
      if (!comunicacaoId) {
        throw new Error('ID da comunicação é obrigatório');
      }

      const validId = String(comunicacaoId).trim();
      if (!validId) {
        throw new Error('ID da comunicação inválido');
      }

      console.log('🔍 Marcando comunicação como lida:', validId);

      const comunicacaoRef = doc(db, COLLECTIONS.PJE_COMUNICACOES, validId);
      await updateDoc(comunicacaoRef, {
        isRead: true,
        readAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('✅ Comunicação marcada como lida:', validId);
    } catch (error) {
      console.error('❌ Erro ao marcar comunicação como lida:', error);
      console.error('❌ ID recebido:', comunicacaoId, typeof comunicacaoId);
      throw error;
    }
  }

  /**
   * Atualizar comunicação
   */
  static async updateComunicacao(userId, comunicacaoId, updates) {
    try {
      const validId = String(comunicacaoId).trim();
      const comunicacaoRef = doc(db, COLLECTIONS.PJE_COMUNICACOES, validId);
      await updateDoc(comunicacaoRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
      console.log('✅ Comunicação atualizada:', validId);
    } catch (error) {
      console.error('❌ Erro ao atualizar comunicação:', error);
      throw error;
    }
  }

  // ==================== CONFIGURAÇÕES DE NOTIFICAÇÃO ====================

  /**
   * Buscar configurações de notificação do usuário
   */
  static async getUserNotificationSettings(userId) {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.notificationSettings || {
          email: true,
          push: true,
          prazosHoje: true,
          prazosAmanha: true,
          prazosSemana: true,
          novasComunicacoes: true
        };
      }
      
      // Retornar configurações padrão se não existir
      return {
        email: true,
        push: true,
        prazosHoje: true,
        prazosAmanha: true,
        prazosSemana: true,
        novasComunicacoes: true
      };
    } catch (error) {
      console.error('❌ Erro ao buscar configurações de notificação:', error);
      // Retornar configurações padrão em caso de erro
      return {
        email: true,
        push: true,
        prazosHoje: true,
        prazosAmanha: true,
        prazosSemana: true,
        novasComunicacoes: true
      };
    }
  }

  /**
   * Atualizar configurações de notificação do usuário
   */
  static async updateUserNotificationSettings(userId, notificationSettings) {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, {
        notificationSettings,
        updatedAt: serverTimestamp()
      });
      console.log('✅ Configurações de notificação atualizadas');
    } catch (error) {
      console.error('❌ Erro ao atualizar configurações de notificação:', error);
      throw error;
    }
  }

  // ==================== TENANTS (MULTITENANCY) ====================

  /**
   * 🏢 Criar tenant
   */
  static async createTenant(tenantId, tenantData) {
    try {
      const tenantRef = doc(db, COLLECTIONS.TENANTS, tenantId);
      await setDoc(tenantRef, tenantData);
      console.log('✅ Tenant criado:', tenantId);
      return tenantData;
    } catch (error) {
      console.error('❌ Erro ao criar tenant:', error);
      throw error;
    }
  }

  /**
   * 🔍 Buscar tenant por ID
   */
  static async getTenant(tenantId) {
    try {
      const tenantRef = doc(db, COLLECTIONS.TENANTS, tenantId);
      const tenantSnap = await getDoc(tenantRef);
      
      if (!tenantSnap.exists()) {
        throw new Error('Tenant não encontrado');
      }
      
      return { id: tenantSnap.id, ...tenantSnap.data() };
    } catch (error) {
      console.error('❌ Erro ao buscar tenant:', error);
      throw error;
    }
  }

  /**
   * 🔄 Atualizar tenant
   */
  static async updateTenant(tenantId, updates) {
    try {
      const tenantRef = doc(db, COLLECTIONS.TENANTS, tenantId);
      await updateDoc(tenantRef, updates);
      console.log('✅ Tenant atualizado:', tenantId);
    } catch (error) {
      console.error('❌ Erro ao atualizar tenant:', error);
      throw error;
    }
  }

  /**
   * 📋 Listar tenants (Admin)
   */
  static async listTenants(filters = {}) {
    try {
      const tenantsRef = collection(db, COLLECTIONS.TENANTS);
      let q = query(tenantsRef, orderBy('createdAt', 'desc'));
      
      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }
      
      if (filters.plan) {
        q = query(q, where('plan', '==', filters.plan));
      }
      
      const querySnapshot = await getDocs(q);
      const tenants = [];
      querySnapshot.forEach((doc) => {
        tenants.push({ id: doc.id, ...doc.data() });
      });
      
      return tenants;
    } catch (error) {
      console.error('❌ Erro ao listar tenants:', error);
      throw error;
    }
  }

  /**
   * 🔄 Atualizar usuário
   */
  static async updateUser(userId, updates) {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      await updateDoc(userRef, updates);
      console.log('✅ Usuário atualizado:', userId);
    } catch (error) {
      console.error('❌ Erro ao atualizar usuário:', error);
      throw error;
    }
  }
}
